import { Typography, Box, Button } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import * as queries from 'graphql/queries';
import * as mutation from 'graphql/mutations';
import useOrgData from 'application/hooks/useOrgData';
import { LoadingSpinner } from '../Integrations/components/LoadingSpinner';
import { GamifyToast } from 'application/common/CustomToasts';
// const { v4: uuidv4 } = require('uuid');
import ReviewRequestForm from './configurationForms/ReviewRequestForm';

export interface ReviewPageLink {
  ctaText: string;
  url: string;
}

export interface ReviewConfig {
  defaultMessage: string;
  fromEmail: string;
  reviewDisplayText: string;
  reviewPageUrl: ReviewPageLink[];
  new?: boolean;
}

export default function MessagesConfig() {
  const [reviewConfig, setReviewConfig] = useState<ReviewConfig | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fetchedConfig, setFetchedConfig] = useState<any>(false);
  const { orgData } = useOrgData();

  const updateConfig = async (config: ReviewConfig) => {
    try {
      await API.graphql(
        graphqlOperation(mutation.updatePreferences, {
          input: {
            id: `${orgData?.id}:review-request-config`,
            preferences: JSON.stringify(config),
          },
        })
      );
      setReviewConfig(config);
      GamifyToast.success('Review request config updated successfully');
    } catch (err) {
      console.error('Error updating review config', err);
      GamifyToast.error('Failed to update review config');
    }
  };
  const createNewConfig = async (config: ReviewConfig) => {
    try {
      await API.graphql(
        graphqlOperation(mutation.createPreferences, {
          input: {
            id: `${orgData?.id}:review-request-config`,
            preferences: JSON.stringify(config),
            name: 'review-request-config',
            orgID: orgData?.id,
            preferenceType: 'reviewRequest',
            isDeleted: false,
            isHidden: false,
            entityType: 'org',
            entityID: orgData?.id,
          },
        })
      );
      setReviewConfig(config);
      GamifyToast.success('Review request config created successfully');
    } catch (err) {
      console.error('Error creating review config', err);
      GamifyToast.error('Failed to create review config');
    }
  };

  const handleSubmit = async (config: ReviewConfig) => {
    if (reviewConfig?.new) {
      await createNewConfig(config);
    } else {
      await updateConfig(config);
    }
  };

  const createConfig = () => {
    setReviewConfig({
      defaultMessage: '',
      fromEmail: '',
      reviewDisplayText: '',
      reviewPageUrl: [{ ctaText: '', url: '' }],
      new: true,
    });
  };

  useEffect(() => {
    const fetchReviewConfig = async () => {
      setIsLoading(true);
      try {
        const res = await API.graphql(
          graphqlOperation(queries.getPreferences, {
            // @ts-ignore
            id: `${orgData?.id}:review-request-config`,
          })
        );
        // @ts-ignore
        const data = res?.data?.getPreferences || null;
        if (data) {
          const config = JSON.parse(data.preferences);
          setReviewConfig(config);
        }
      } catch (err) {
        console.error('Error fetching review config', err);
        GamifyToast.error('Failed to fetch review config');
      } finally {
        setIsLoading(false);
        setFetchedConfig(true);
      }
    };
    fetchReviewConfig();
  }, []);

  if (isLoading) {
    return <LoadingSpinner />;
  }
  return (
    <Box
      sx={(theme) => ({
        mt: 4,
        mb: 4,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.custom?.stroke}`,
        borderRadius: '10px',
        minHeight: '60vh',
      })}
    >
      {fetchedConfig && !reviewConfig && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexGrow: 1,
            height: '100%',
            flexDirection: 'column',
            gap: 1,
            // backgroundColor: 'green',
          }}
        >
          <Typography
            sx={(theme) => ({
              fontSize: 18,
              fontWeight: 700,
              color: theme.custom?.text,
            })}
          >
            No presets found
          </Typography>
          <Typography
            sx={(theme) => ({
              fontSize: 16,
              fontWeight: 400,
              color: theme.custom?.textLight,
            })}
          >
            These messages help your team to get better reviews
          </Typography>
          <Button
            onClick={createConfig}
            variant="outlined"
            color="primary"
            sx={(theme) => ({
              border: `1px solid ${theme.custom?.text}`,
              color: theme.custom?.text,
              mt: 2,

              '&:hover': {
                cursor: 'pointer',
                backgroundColor: theme.custom?.text,
                color: theme.custom?.background1,
                '& svg path': {
                  fill: theme.custom?.background1,
                },
              },
            })}
            startIcon={
              <Box
                component="svg"
                width="20"
                height="18"
                viewBox="0 0 20 18"
                fill="none"
                sx={(theme) => ({
                  '& path': {
                    fill: theme.custom?.text,
                  },
                })}
              >
                <path d="M15 4H16V5C16 5.26522 16.1054 5.51957 16.2929 5.70711C16.4804 5.89464 16.7348 6 17 6C17.2652 6 17.5196 5.89464 17.7071 5.70711C17.8946 5.51957 18 5.26522 18 5V4H19C19.2652 4 19.5196 3.89464 19.7071 3.70711C19.8946 3.51957 20 3.26522 20 3C20 2.73478 19.8946 2.48043 19.7071 2.29289C19.5196 2.10536 19.2652 2 19 2H18V1C18 0.734784 17.8946 0.48043 17.7071 0.292893C17.5196 0.105357 17.2652 0 17 0C16.7348 0 16.4804 0.105357 16.2929 0.292893C16.1054 0.48043 16 0.734784 16 1V2H15C14.7348 2 14.4804 2.10536 14.2929 2.29289C14.1054 2.48043 14 2.73478 14 3C14 3.26522 14.1054 3.51957 14.2929 3.70711C14.4804 3.89464 14.7348 4 15 4ZM19 8C18.7348 8 18.4804 8.10536 18.2929 8.29289C18.1054 8.48043 18 8.73478 18 9V15C18 15.2652 17.8946 15.5196 17.7071 15.7071C17.5196 15.8946 17.2652 16 17 16H3C2.73478 16 2.48043 15.8946 2.29289 15.7071C2.10536 15.5196 2 15.2652 2 15V5.41L7.88 11.3C8.4425 11.8618 9.205 12.1774 10 12.1774C10.795 12.1774 11.5575 11.8618 12.12 11.3L14.59 8.83C14.7783 8.6417 14.8841 8.3863 14.8841 8.12C14.8841 7.8537 14.7783 7.5983 14.59 7.41C14.4017 7.2217 14.1463 7.11591 13.88 7.11591C13.6137 7.11591 13.3583 7.2217 13.17 7.41L10.7 9.88C10.5131 10.0632 10.2618 10.1659 10 10.1659C9.73825 10.1659 9.48693 10.0632 9.3 9.88L3.41 4H11C11.2652 4 11.5196 3.89464 11.7071 3.70711C11.8946 3.51957 12 3.26522 12 3C12 2.73478 11.8946 2.48043 11.7071 2.29289C11.5196 2.10536 11.2652 2 11 2H3C2.20435 2 1.44129 2.31607 0.87868 2.87868C0.316071 3.44129 0 4.20435 0 5V15C0 15.7956 0.316071 16.5587 0.87868 17.1213C1.44129 17.6839 2.20435 18 3 18H17C17.7956 18 18.5587 17.6839 19.1213 17.1213C19.6839 16.5587 20 15.7956 20 15V9C20 8.73478 19.8946 8.48043 19.7071 8.29289C19.5196 8.10536 19.2652 8 19 8Z" />
              </Box>
            }
          >
            Create preset message
          </Button>
        </Box>
      )}
      {fetchedConfig && reviewConfig && (
        <ReviewRequestForm
          config={reviewConfig}
          onSubmit={handleSubmit}
          onCancel={() => setReviewConfig(null)}
        />
      )}
    </Box>
  );
}
